import 'components/sidebar/index.css'
import './index.css'
import './login-loader.css'

import cn from 'clsx'
import React, { useCallback, useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useLocation } from 'react-router-dom'

import api from '@disco/api/server'
import {
  autologinUser,
  loginUser,
} from '@disco/state-management/actions/UserActions'
import {
  getRequestsCompleted,
  getRequestsTotal,
} from '@disco/state-management/selectors/app'
import { isShortenedDomainUrl } from '@disco/utils/hostnames'
import { newlinesAsHtml } from '@disco/utils/newlines'

import { useRegionCookieConsentContext } from 'contexts/cookie-consent'

import BrowserModal from 'components/browser-modal'
import LoginForm from 'components/common/login-form'
import CookiesDisabled from 'components/cookies-disabled'
import DocumentHeadTags from 'components/document-head-tags'
import {
  DiscoStackLoader,
  ProgressBarLoader,
} from 'components/ds'
import CookieBanner from 'components/modals/cookie-banner'
import Heading from 'components/ui/heading'
import Logo from 'components/ui/logo'

import ShortlinkNotFound from 'pages/shortlink-not-found'

import { useDispatch, useSelector } from 'hooks/use-redux'

type ProductUpdate = {
  title: string
  login_page_content: string
}

const Login = (): React.ReactElement<'div'> | React.ReactNode => {
  const routerLocation = useLocation()

  // Parse query parameters from URLSearchParams
  const searchParams = new URLSearchParams(routerLocation.search)
  const query = {
    return: searchParams.get('return') || undefined,
    next: searchParams.get('next') || undefined,
    sessionExpired: searchParams.get('sessionExpired') || undefined,
    reason: searchParams.get('reason') || undefined,
  }

  // Create compatible location object for legacy components
  const location = {
    ...routerLocation,
    query,
  }
  const user = useSelector(state => state.user)
  const numCompleted = useSelector(getRequestsCompleted)
  const numTotal = useSelector(getRequestsTotal)
  const dispatch = useDispatch()
  const [productUpdate, setProductUpdate] =
    useState<ProductUpdate | null | undefined>()
  const cookieConsentContext = useRegionCookieConsentContext()
  const isDesktop = useMediaQuery({
    query: '(min-width: 701px)',
  })

  const handleForgotPassword = useCallback(
    (email: string, returnUrl: string) => {
      return api.sendPasswordRecoveryInstructions(email, returnUrl)
    },
    [],
  )

  useEffect(() => {
    dispatch(autologinUser(query.return || query.next))
  }, [])

  useEffect(() => {
    if (!isDesktop) return
    if (productUpdate) return
    // TODO: move data into reducer (here and in `<ProductUpdatesModal>`)
    api
      .getProductUpdates()
      .then(({ items }) =>
        setProductUpdate(items.find(i => i.show_on_login_page)),
      )
      .catch(() => {})
  }, [isDesktop, productUpdate])

  if (isShortenedDomainUrl()) {
    return <ShortlinkNotFound />
  }

  if (user.autologgingIn || user.loggedIn) {
    const progressValue = (Math.min(numCompleted, numTotal) / numTotal) * 100

    return (
      <>
        <ProgressBarLoader value={progressValue} />
        <div className="LoginPage_LoaderCont">
          <div className="LoginPage_Loader">
            <DiscoStackLoader />
          </div>
        </div>
      </>
    )
  }

  return (
    <div className="LoginPage">
      <div className="LoginPage_Title">
        {/* TODO: extract Sidebar_Logo into logo component */}
        <div className="Sidebar_Head">
          <Logo className="Sidebar_Logo" />
        </div>
      </div>
      <div className="LoginPage_Container">
        <DocumentHeadTags title="Login | DISCO" />
        <CookiesDisabled />
        <BrowserModal />
        <CookieBanner location="Login" />

        <div className="LoginPage_FormContainer">
          <LoginForm
            user={user}
            location={location}
            onAutoLoginUser={returnPath => {
              return dispatch(autologinUser(returnPath))
            }}
            onLoginUser={(loginDetails, returnPath) => {
              return dispatch(
                loginUser(loginDetails, returnPath, null, {
                  cookieConsentContext,
                }),
              )
            }}
            onForgotPassword={handleForgotPassword}
            onGetSignupUrl={() => Promise.resolve('https://disco.ac/signup')}
            onGetBusiness={async () => {
              return await api.getCurrentBusinessPublic()
            }}
          />
        </div>

        {isDesktop && (
          <div className="LoginPage_NewsCont LoginPage_NewsBg">
            <div
              className={cn('LoginPage_NewsBlock', {
                LoginPage_NewsLoaded: Boolean(productUpdate),
              })}
            >
              <div className="LoginPage_HeadingCont">What’s new</div>
              <Heading size={700}>{productUpdate?.title}</Heading>
              <div className="LoginPage_NewsBody">
                <article className="UpdatesListItem_Block">
                  <div
                    className="UpdatesListItem_Content"
                    dangerouslySetInnerHTML={{
                      __html: newlinesAsHtml(
                        productUpdate?.login_page_content || '',
                      ),
                    }}
                  />
                </article>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Login
